// Element library logic, pure: handles, the data form per subtype, grouping, reference choice for
// thumbnails, and recognising the "version is frozen" answer.
import type { Element, ElementReference, ElementSubtype, ReferenceRole } from "./types.ts";

export const SUBTYPES: readonly ElementSubtype[] = ["character", "location", "prop", "style"];

export const SUBTYPE_LABEL: Record<ElementSubtype, string> = {
  character: "Characters",
  location: "Locations",
  prop: "Props",
  style: "Styles",
};

/** Roles an uploaded image can have (voice and LoRA are not images). */
export const IMAGE_ROLES: readonly ReferenceRole[] = [
  "front",
  "three_quarter",
  "profile",
  "back",
  "full_body",
  "turnaround",
  "expression",
  "detail",
  "wide",
  "other",
];

/** Views a sheet can be generated for in one click. */
export const SHEET_BUTTONS: readonly { role: ReferenceRole; label: string }[] = [
  { role: "front", label: "front" },
  { role: "profile", label: "profile" },
  { role: "full_body", label: "full body" },
];

export const HANDLE_RE = /^[a-z0-9][a-z0-9-]*$/;

/** `Anna Reyes` → `anna-reyes` (the handle format the API accepts, at most 80 characters). */
export function slugify(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/g, "");
}

export const isValidHandle = (h: string): boolean => h.length <= 80 && HANDLE_RE.test(h);

// ─── the data form ───────────────────────────────────────────────────────────

export interface ElementFields {
  /** data.prompt: the hand-tuned look fragment the compiler uses first. */
  prompt: string;
  wardrobe: string;
  voice_id: string;
  /** Camera preset id (style elements). */
  preset: string;
}

/** Which fields a subtype's form shows. */
export const FIELDS_OF: Record<ElementSubtype, readonly (keyof ElementFields)[]> = {
  character: ["prompt", "wardrobe", "voice_id"],
  location: ["prompt"],
  prop: ["prompt"],
  style: ["preset", "prompt"],
};

const str = (v: unknown) => (typeof v === "string" ? v : "");

export function fieldsFromData(data: Record<string, unknown> | undefined): ElementFields {
  const d = data ?? {};
  return {
    prompt: str(d.prompt) || str(d.look) || str(d.description),
    wardrobe: str(d.wardrobe) || str(d.costume),
    voice_id: str(d.voice_id) || str(d.voiceId),
    preset: str(d.preset),
  };
}

/**
 * Form fields → `element_version.data`. Starts from `base` so keys the form does not know survive an
 * edit; an empty field removes its key; fields of other subtypes are left out.
 */
export function buildData(
  subtype: ElementSubtype,
  fields: Partial<ElementFields>,
  base: Record<string, unknown> = {},
): Record<string, unknown> {
  const out = { ...base };
  for (const k of FIELDS_OF[subtype]) {
    const v = (fields[k] ?? "").trim();
    if (v) out[k] = v;
    else delete out[k];
    if (k === "prompt") {
      delete out.look;
      if (!v) delete out.description;
    }
  }
  return out;
}

/** Every field of the subtype's form, empty ones as "": a fork merges shallowly, so clearing needs an explicit empty value. */
export function dataPatch(subtype: ElementSubtype, fields: Partial<ElementFields>): Record<string, string> {
  return Object.fromEntries(FIELDS_OF[subtype].map((k) => [k, (fields[k] ?? "").trim()]));
}

// ─── lists ───────────────────────────────────────────────────────────────────

export function groupBySubtype(
  elements: readonly Element[],
): { subtype: ElementSubtype; items: Element[] }[] {
  return SUBTYPES.map((subtype) => ({
    subtype,
    items: elements
      .filter((e) => e.subtype === subtype && !e.archived_at)
      .sort((a, b) => a.name.localeCompare(b.name)),
  })).filter((g) => g.items.length > 0);
}

/** The reference to show as a thumbnail: canonical first, then by position, never voice or LoRA. */
export function thumbReference(refs: readonly ElementReference[]): ElementReference | undefined {
  return [...refs]
    .filter((r) => r.role !== "voice" && r.role !== "lora")
    .sort(
      (a, b) =>
        Number(b.canonical) - Number(a.canonical) ||
        (a.position ?? 0) - (b.position ?? 0) ||
        a.created_at.localeCompare(b.created_at),
    )[0];
}

/**
 * The name of a reference as shown on its tile: the role ("Front"), joined with its label when the label
 * only adds to it ("front sheet" gives "Front sheet", never "Front front sheet"). A label that says
 * something else stays beside the role.
 */
export function referenceName(
  role: string,
  label: string | null | undefined,
): { title: string; extra: string | null } {
  const r = role.replace(/_/g, " ");
  const l = (label ?? "").trim();
  if (!l) return { title: r, extra: null };
  const lower = l.toLowerCase();
  if (lower === r) return { title: r, extra: null };
  if (lower.startsWith(`${r} `)) return { title: l, extra: null };
  return { title: r, extra: l };
}

/** Role of a sheet take from its label (`front`, `full body`, `three-quarter`…), default front. */
export function guessRole(label: string | null | undefined): ReferenceRole {
  const l = (label ?? "").toLowerCase().replace(/[-\s]+/g, "_");
  return IMAGE_ROLES.find((r) => r !== "other" && l.includes(r)) ?? "front";
}

/**
 * A version's origin in a word or two, for "v1 · Imported · 9 min ago". Machine notes ("imported from
 * pipeline serie-x/ep01", "accepted front from take …", "new version for a reference") become plain
 * words; a note a person wrote stays as written.
 */
export function versionOrigin(note: string | null | undefined, version: number): string {
  const n = (note ?? "").trim();
  if (!n) return version === 1 ? "First version" : "New version";
  if (/^imported\b/i.test(n)) return "Imported";
  if (/^new version\b/i.test(n)) return "New version";
  if (/^accepted\b.*\bfrom take\b/i.test(n)) return "Sheet signed off";
  return n;
}

// ─── errors ──────────────────────────────────────────────────────────────────

/** The answer to adding a reference to a version a generation already used (409, frozen). */
export function isFrozenError(e: unknown): boolean {
  if (!e || typeof e !== "object") return false;
  const o = e as { status?: unknown; code?: unknown; message?: unknown };
  if (o.status !== 409) return false;
  return (
    o.code === "element_version_frozen" || o.code === "immutable" || /frozen/i.test(String(o.message ?? ""))
  );
}

// ─── recognising a dropped photo ─────────────────────────────────────────────

export type QuickKind = "character" | "location" | "prop";

/** File-name words that say what a photo shows; the first word that matches wins. */
const KIND_WORDS: readonly [QuickKind, readonly string[]][] = [
  [
    "character",
    ["chr", "char", "character", "cast", "cre", "creature", "actor", "person", "face", "portrait"],
  ],
  [
    "location",
    ["loc", "location", "set", "env", "environment", "bg", "background", "place", "interior", "exterior"],
  ],
  ["prop", ["prop", "props", "obj", "object", "item", "thing"]],
];

/** Words that are noise in a file name: versions, takes, sizes, camera counters. */
const NOISE = /^(v\d+|take\d*|final|copy|sheet|ref|front|side|back|\d+x\d+|img|dsc|image|photo|\d+)$/;

/**
 * Kind and name from a file name, the way a crew names its files: `dd_chr_robin_v3.png` is the cast member
 * "Robin", `loc-titan-city.jpg` the set "Titan city". A short prefix in front of the kind word (a production
 * code like `dd_`) is dropped. `sure` is false when no kind word was found and the kind is only a default.
 */
export function guessFromFileName(
  fileName: string,
  fallback: QuickKind = "character",
): { kind: QuickKind; name: string; sure: boolean } {
  const words = fileName
    .replace(/\.[a-z0-9]{2,5}$/i, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  let kind: QuickKind | null = null;
  let at = -1;
  for (let i = 0; i < words.length && kind === null; i++)
    for (const [k, list] of KIND_WORDS)
      if (list.includes(words[i]!)) {
        kind = k;
        at = i;
        break;
      }
  // a production code before the kind word (dd_chr_…) is not part of the name
  const rest = (at >= 0 ? words.slice(at + 1) : words).filter((w) => !NOISE.test(w));
  const name = rest.join(" ");
  return {
    kind: kind ?? fallback,
    name: name ? name[0]!.toUpperCase() + name.slice(1) : "",
    sure: kind !== null,
  };
}
